import React, { useEffect, useState } from "react";
import {
  ChevronLeft,
  ChevronRight,
  Facebook,
  Instagram,
  Twitter,
  Youtube,
} from "lucide-react";
import { getFooter } from "../../services/api";
import { useInstagram } from "../../contexts/InstagramContexts";

interface media {
  id: string;
  media_url: string;
  caption?: string;
  timestamp: string;
  permalink: string;
}

interface FooterData {
  facebook_link?: string;
  instagram_link?: string;
  youtube_link?: string;
  tiktok_link?: string;
  twitter_link?: string;
}

const BeautyInMotion: React.FC = () => {
  const { media, loading, error } = useInstagram();
  const [currentStoryIndex, setCurrentStoryIndex] = useState(0);
  const [footerData, setFooterData] = useState<FooterData>({});

  useEffect(() => {
    const fetchData = async () => {
      try {
        const footerResponse = await getFooter();
        setFooterData(footerResponse.data.data[0] || {});
      } catch (err) {
        console.error("Failed to fetch data:", err);
      } finally {
      }
    };

    fetchData();
  }, []);

  const handleNext = () => {
    setCurrentStoryIndex((prevIndex) =>
      media.length > 0 ? (prevIndex + 1) % media.length : 0
    );
  };

  const handlePrev = () => {
    setCurrentStoryIndex((prevIndex) =>
      media.length > 0
        ? (prevIndex - 1 + media.length) % media.length
        : 0
    );
  };

  const getPositionClass = (index: number) => {
    if (media.length === 0) return "hidden";

    const secondPrevIndex =
      (currentStoryIndex - 2 + media.length) % media.length;
    const prevIndex =
      (currentStoryIndex - 1 + media.length) % media.length;
    const nextIndex = (currentStoryIndex + 1) % media.length;
    const secondNextIndex = (currentStoryIndex + 2) % media.length;

    if (index === currentStoryIndex)
      return "z-20 scale-100 opacity-100 translate-x-0";
    if (index === prevIndex) return "z-10 scale-75 blur-sm -translate-x-1/3";
    if (index === nextIndex) return "z-10 scale-75 blur-sm translate-x-1/3";
    if (index === secondPrevIndex)
      return "z-5 scale-50 blur-sm -translate-x-2/3";
    if (index === secondNextIndex)
      return "z-5 scale-50 blur-sm translate-x-2/3";
    return "z-0 scale-50 opacity-0";
  };

  // Format timestamp to readable date
  const formatDate = (timestamp: string) => {
    const date = new Date(timestamp);
    return date.toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
    });
  };

  if (error) {
    return (
      <div className="bg-white flex flex-col items-center pt-16 pb-12">
        <div className="text-center text-red-500">{error}</div>
      </div>
    );
  }

  return (
    <div className="bg-white flex flex-col items-center pt-16 pb-12">
      <div className="lg:mb-8 h-[30px] w-[280px] lg:h-[40px] lg:w-[328px] lg:w-full flex items-center justify-center mx-auto">
        <img src="/images/beautyInMotion.svg" alt="Beauty in Motion" />
      </div>

      <div className="max-w-[1120px] flex flex-col lg:flex-row items-center justify-between gap-4 w-full">
        <div className="lg:flex-1">
          <div className="relative flex items-center justify-center w-[328px] lg:w-full lg:max-w-5xl">
            <button
              onClick={handlePrev}
              className="absolute left-0 top-1/2 -translate-y-1/2 z-30 p-2 rounded-full bg-white shadow-lg"
            >
              <ChevronLeft size={24} className="text-gray-700" />
            </button>

            <div className="relative flex items-center justify-center w-[328px] lg:w-full lg:max-w-6xl h-[468px] lg:h-[600px] overflow-hidden my-6 lg:my-0">
              {media.length > 0 ? (
                media.map((media, index) => (
                  <div
                    key={media.id}
                    className={`absolute transition-all duration-700 ease-in-out transform ${getPositionClass(
                      index
                    )}`}
                  >
                    <a
                      href={media.permalink}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <img
                        src={media.media_url}
                        alt="Instagram post"
                        className="w-[270px] h-[444px] object-cover rounded-lg"
                      />
                    </a>
                  </div>
                ))
              ) : (
                <div className="text-center">No Instagram posts available</div>
              )}
            </div>

            <button
              onClick={handleNext}
              className="absolute right-0 top-1/2 -translate-y-1/2 z-30 p-2 rounded-full bg-[#B87775] shadow-lg"
            >
              <ChevronRight size={24} className="text-white" />
            </button>
          </div>
        </div>

        <div
          className="lg:flex-1 text-center lg:mt-8 px-4"
          style={{ fontFamily: "Cormorant Infant, serif" }}
        >
          {/* <h2 className="hidden lg:flex text-[33px] text-center mb-4 flex items-center justify-center">
            Featured Gallery
          </h2> */}
          {media.length > 0 ? (
            <>
              <div className="mb-2 max-w-[300px] lgmax-w-[420px] mx-auto text-[12px] lg:text-[18px] font-[500] px-2 lg:px-0 break-words whitespace-pre-line">
                Elevating beauty through artistry and elegance. 
                Discover moments of transformation and timeless grace.
              </div>
              {/* <p className="text-[12px] lg:text-[18px] font-[500] italic">
                ~ Posted on{" "}
                {formatDate(media[currentStoryIndex].timestamp)}
              </p> */}
            </>
          ) : (
            <p>No Instagram posts available</p>
          )}
        </div>
      </div>

      <div className="flex items-center justify-center space-x-2 lg:space-x-4 py-3">
        <p>For more content</p>

        {footerData.facebook_link && (
          <a
            href={footerData.facebook_link}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 hover:text-blue-800"
          >
            <Facebook className="h-4 w-4 lg:h-6 lg:w-6" />
          </a>
        )}

        {footerData.instagram_link && (
          <a
            href={footerData.instagram_link}
            target="_blank"
            rel="noopener noreferrer"
            className="text-pink-600 hover:text-pink-800"
          >
            <Instagram className="h-4 w-4 lg:h-6 lg:w-6" />
          </a>
        )}

        {footerData.youtube_link && (
          <a
            href={footerData.youtube_link}
            target="_blank"
            rel="noopener noreferrer"
            className="text-red-600 hover:text-red-800"
          >
            <Youtube className="h-4 w-4 lg:h-7 lg:w-7" />
          </a>
        )}

        {footerData.tiktok_link && (
          <a
            href={footerData.tiktok_link}
            target="_blank"
            rel="noopener noreferrer"
            className="text-black hover:text-gray-800"
          >
            <img
              src="/images/tiktok.svg"
              alt="Tiktok"
              className="h-4 w-4 lg:h-6 lg:w-6"
            />
          </a>
        )}

        {footerData.twitter_link && (
          <a
            href={footerData.twitter_link}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-400 hover:text-blue-600"
          >
            <Twitter className="h-4 w-4 lg:h-6 lg:w-6" />
          </a>
        )}
      </div>
    </div>
  );
};

export default BeautyInMotion;
